/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { useState } from 'react';

export interface ISignUpInputProps {
    id?: string;
    hasFocus?: boolean;
    type: any;
    value?: string;
    pattern?: string;
    className: string;
    maxLength?: string;
    onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
    errorMessage?: string;
    placeHolder?: string;
    ariaLabel?: string;
}
export interface ISignUpMobileNumberInputProps {
    id?: string;
    hasFocus?: boolean;
    type: any;
    value?: string;
    pattern?: string;
    className: string;
    maxLength?: string;
    onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
    errorMessage?: string;
    placeHolder?: string;
    ariaLabel?: string;
    country: string | undefined;
}

export interface ISignUpInput {
    key: string;
    AddressItem: INodeProps;
    label: React.ReactNode;
    alert: React.ReactNode;
    input: React.ReactNode;
}

const GetMaxLength = (maxLength?: string): number | undefined => {
    if (maxLength) {
        const parsedMaxLength = Number.parseInt(maxLength, 10);
        if (!isNaN(parsedMaxLength)) {
            return parsedMaxLength;
        }
    }

    return undefined;
};

const SignUpInput: React.FC<ISignUpInputProps> = ({ id, type, value, pattern, className, maxLength, onChange, ariaLabel }) => {
    const updateValue = () => {
        if (id) {
            const element = document.getElementById(id);
            element?.dispatchEvent(new KeyboardEvent('keypress', { which: -1, keyCode: -1 } as KeyboardEventInit));
        }
    };
    const callOnChange = id === 'newPassword' || id === 'reenterPassword';
    return (
        <input
            id={id}
            type={type}
            value={value}
            className={`${className}-input ${className}-${id}`}
            pattern={pattern !== '' ? pattern : undefined}
            aria-required='true'
            maxLength={GetMaxLength(maxLength)}
            onChange={callOnChange ? updateValue : onChange}
            aria-label={ariaLabel ? ariaLabel : ''}
        />
    );
};

export default SignUpInput;

function hideError(elemId: any) {
    var item = document.querySelector(`.ms-sign-up__account-item-${elemId} .ms-sign-up__item-error`);
    if (item) {
        item.classList.remove('show');
        item.innerHTML = '';
        item.setAttribute('aria-hidden', 'true');
    }
}
function showError(elemId: any, message: any) {
    var item = document.querySelector(`.ms-sign-up__account-item-${elemId} .ms-sign-up__item-error`);
    if (item) {
        item.classList.add('show');
        item.innerHTML = message;
        item.setAttribute('aria-hidden', 'false');
    }
}

export const SignUpMobileNumberInput: React.FC<ISignUpMobileNumberInputProps> = ({
    id,
    type,
    value,
    pattern,
    className,
    maxLength,
    onChange,
    errorMessage,
    placeHolder,
    ariaLabel,
    country
}) => {
    const [showMobileNumber, setShowMobileNumber] = useState('');
    const [mobileNumber, setMobileNumber] = useState('');
    const onInputChange = (e: any) => {
        // setValue(e.target.value);
        const input = e.target.value.replace(/\D/g, '').substring(0, 10);
        if (country === 'CAN' || country === 'USA') {
            const areaCode = input.substring(0, 3);
            const middle = input.substring(3, 6);
            const last = input.substring(6, 10);
            let viewInput = '';
            if (input.length > 6) {
                viewInput = `${areaCode} ${middle} ${last}`;
            } else if (input.length > 3) {
                viewInput = `${areaCode} ${middle}`;
            } else if (input.length > 0) {
                viewInput = `${areaCode}`;
            }
            setShowMobileNumber(viewInput);
            setMobileNumber(input);
        } else {
            const areaCode = input.substring(0, 4);
            const middle = input.substring(4, 7);
            const last = input.substring(7, 10);
            let viewInput = '';
            if (input.length > 7) {
                viewInput = `${areaCode} ${middle} ${last}`;
            } else if (input.length > 4) {
                viewInput = `${areaCode} ${middle}`;
            } else if (input.length > 0) {
                viewInput = `${areaCode}`;
            }
            setShowMobileNumber(viewInput);
            setMobileNumber(input);
        }
        if (pattern) {
            const regPattern = new RegExp(pattern);
            if (input && regPattern.test(input) && id) {
                hideError(id);
            }
            if (input && !regPattern.test(input) && id) {
                showError(id, errorMessage);
            }
            if (!input) {
                showError(id, `${ariaLabel} is required`);
            }
        }
    };
    return (
        <>
            <input
                id={`_${id}`}
                type={type}
                value={showMobileNumber}
                className={`${className}-input ${className}-${id}`}
                placeholder={placeHolder ? placeHolder : ''}
                onChange={onInputChange}
                aria-label={ariaLabel ? ariaLabel : ''}
            />
            <input
                id={id}
                type={type}
                value={mobileNumber}
                style={{ display: 'none' }}
                pattern={pattern !== '' ? pattern : undefined}
                aria-required='true'
                minLength={GetMaxLength(maxLength)}
                maxLength={GetMaxLength(maxLength)}
                aria-label={ariaLabel ? ariaLabel : ''}
            />
        </>
    );
};
